import { NH_SIGN_IN_URL, UserMe } from "../api";
import { PageHeader } from "../ui";
import { fundAccess, historyLabel } from "./Pricing";

export function Account({ user }: { user: UserMe }) {
  const t = user.tier;

  return (
    <section className="flex flex-col gap-10">
      {/* ── Profile ─────────────────────── */}
      <div>
        <PageHeader title="Account" description="Your profile and the limits of your plan." />
        <div className="flex flex-col gap-3 p-5 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent/15 text-accent text-sm font-bold uppercase shrink-0">
              {(user.name ?? user.email)[0]}
            </div>
            <div className="min-w-0">
              {user.name && <p className="text-[15px] font-medium text-main truncate">{user.name}</p>}
              <p className="text-sm text-muted truncate">{user.email}</p>
            </div>
            <span className={`ml-auto text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${tierBadge(t.name)}`}>
              {t.display_name}
            </span>
          </div>
        </div>
      </div>

      {/* ── Current plan ────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-main">Your plan</h2>
          <a href="/pricing" className="text-sm font-medium text-accent hover:text-accent-hover">Compare plans →</a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {t.max_funds ? (
            <LimitCard label="Funds this quarter" value={`${user.funds_used ?? 0} of ${t.max_funds} used`} />
          ) : (
            <LimitCard label="Fund access" value={fundAccess(t)} />
          )}
          <LimitCard label="History depth" value={historyLabel(t)} />
          <LimitCard label="Results / request" value={String(t.max_results_per_page)} />
          <LimitCard label="Bulk export" value={t.can_bulk_export ? "Enabled" : "Not available"} muted={!t.can_bulk_export} />
          <LimitCard label="Rate limit" value={`${t.requests_per_minute} req/min`} />
          <LimitCard label="Daily quota" value={`${t.requests_per_day.toLocaleString()} req/day`} />
        </div>
      </div>

      {/* ── NilssonHedge subscribers who signed up by email ── */}
      {t.name === "free" && (
        <div className="flex max-sm:flex-col sm:items-center justify-between gap-4 p-5 rounded-lg bg-card border border-border">
          <div>
            <p className="text-[15px] font-semibold text-main">Already a NilssonHedge subscriber?</p>
            <p className="mt-1 text-sm text-muted">
              Continue with NilssonHedge to get your plan here. It's matched by email, so this works when your
              NilssonHedge email is <strong className="text-main">{user.email}</strong>.
            </p>
          </div>
          <a href={NH_SIGN_IN_URL} className="btn-primary text-white shrink-0 self-start sm:self-auto">Continue with NilssonHedge</a>
        </div>
      )}

      {/* ── Upgrade CTA ─────────────────── */}
      <div className="flex flex-col items-center gap-3 p-6 rounded-lg bg-card border border-border text-center">
        <p className="text-[15px] font-semibold text-main">Need more data or higher rate limits?</p>
        <p className="text-sm text-muted max-w-md">See what each plan includes, then get in touch to change yours.</p>
        <div className="flex flex-wrap justify-center gap-2 mt-1">
          <a href="/pricing" className="btn-default text-main">Compare plans</a>
          <a href="/contact?interest=pro" className="btn-primary text-white">
            Contact us to upgrade
          </a>
        </div>
      </div>
    </section>
  );
}

function LimitCard({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5 p-5 rounded-lg bg-card border border-border">
      <span className="text-sm text-muted">{label}</span>
      <span className={`text-lg font-semibold ${muted ? "text-muted" : "text-main"}`}>{value}</span>
    </div>
  );
}

function tierBadge(name: string) {
  switch (name) {
    case "free": return "bg-muted/15 text-muted";
    case "basic":
    case "plus": return "bg-accent-secondary/15 text-accent-secondary";
    case "pro": return "bg-accent/15 text-accent";
    case "enterprise": return "bg-warning/15 text-warning";
    default: return "bg-muted/15 text-muted";
  }
}
